/** Client for the proxy's plain-English holiday search (POST /holidays/search).
 *  The proxy parses the query, builds the filter and searches bookable
 *  departures; this module only carries the request and the response.
 *
 *  Absent-safe: the proxy and this widget deploy separately, and the route is
 *  switched on per site origin. A 404 means "not available here", so we stop
 *  asking for the rest of the page view and the panel stays hidden. */

export interface DepartureHit {
  id: string;
  title: string;
  url: string;
  image_url?: string;
  image_alt?: string;
  date_label?: string;
  nights?: number;
  price?: number | null;
  price_note?: string;
  spaces?: number;
  is_bookable?: boolean;
  resorts?: string[];
  countries?: string[];
}

export interface Chip {
  field: string;
  value?: string;
  label: string;
}

/** Opaque to the widget: sent back as-is (minus a removed chip) and
 *  re-validated by the proxy. */
export type ParsedQuery = Record<string, unknown>;

export interface HolidayResult {
  hits: DepartureHit[];
  estimatedTotalHits: number;
  applied: Chip[];
  parsed: ParsedQuery;
  residual_q: string;
}

let unavailable = false;

/** Test hook. */
export function _resetAvailability(): void {
  unavailable = false;
}

export async function holidaySearch(
  host: string,
  params: { q: string; parsed?: ParsedQuery; limit?: number },
  fetchImpl: typeof fetch = fetch,
): Promise<HolidayResult | null> {
  if (unavailable) return null;
  try {
    const res = await fetchImpl(`${host.replace(/\/$/, "")}/holidays/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(params),
    });
    if (res.status === 404) {
      unavailable = true;
      return null;
    }
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<HolidayResult>;
    return {
      hits: Array.isArray(body.hits) ? body.hits : [],
      estimatedTotalHits: typeof body.estimatedTotalHits === "number" ? body.estimatedTotalHits : 0,
      applied: Array.isArray(body.applied) ? body.applied : [],
      parsed: body.parsed && typeof body.parsed === "object" ? body.parsed : {},
      residual_q: typeof body.residual_q === "string" ? body.residual_q : "",
    };
  } catch {
    return null; // network/CORS failure: the panel just doesn't show
  }
}

/** The parse with one chip removed: a list value dropped, or a scalar
 *  cleared. Removing the date chip clears both ends of the window. */
export function withoutChip(parsed: ParsedQuery, chip: Chip): ParsedQuery {
  const next: ParsedQuery = { ...parsed };
  const cur = next[chip.field];
  if (Array.isArray(cur) && chip.value !== undefined) {
    next[chip.field] = cur.filter((v) => v !== chip.value);
  } else if (chip.field === "date_from" || chip.field === "date_to") {
    next.date_from = null;
    next.date_to = null;
  } else if (chip.field === "nights_min" || chip.field === "nights_max") {
    next.nights_min = null;
    next.nights_max = null;
  } else {
    next[chip.field] = null;
  }
  return next;
}

/** "£1,399pp", or "" when there's no price. */
export function priceLabel(hit: DepartureHit): string {
  return typeof hit.price === "number" && hit.price > 0 ? `£${Math.round(hit.price).toLocaleString("en-GB")}pp` : "";
}

/** "Only 2 places left" for low availability, else "". */
export function availabilityLabel(hit: DepartureHit): string {
  const s = hit.spaces ?? 0;
  if (!hit.is_bookable || s <= 0) return "";
  return s <= 3 ? `Only ${s} place${s === 1 ? "" : "s"} left` : "";
}
